import type Immutable from 'immutable'
import type { RootStateOrAny } from 'react-redux'

import { createDisabledSlotId, getDisabledSlotDates } from './boxSummary'

type ResolveSlotIdOptions = {
  // The deeplinked core slot id, if any.
  coreSlotId?: string | null
  // When true, a missing or unmatched coreSlotId falls back to the day's default
  // bookable slot (then the first). When false, an explicit-but-unmatched
  // coreSlotId resolves to undefined so the caller can bounce instead. Defaults
  // to false to preserve the stricter one-off (TRX) hydration behaviour.
  fallbackToDefault?: boolean
}

/**
 * Resolve which slot to land on for a given delivery day.
 *
 * Returns the internal slot id (`slot.get('id')`) of: the slot matching
 * `coreSlotId` when it is bookable; otherwise — depending on `fallbackToDefault`
 * — the day's default bookable slot, then the first. Returns `undefined` when no
 * usable slot is found.
 *
 * "Bookable" means the slot isn't one-off-disabled (`getDisabledSlotDates`);
 * past-cutoff slots are already excluded from the store at load time
 * (`getAvailableDeliveryDays`). Shared by the standard `/menu` deeplink path
 * (`setSlotFromIds`) and the TRX one-off hydration path (`resolveSlotFromIds`)
 * so both pick slots identically.
 */
export const resolveSlotIdForDay = (
  state: RootStateOrAny,
  day: Immutable.Map<string, any>,
  { coreSlotId, fallbackToDefault = false }: ResolveSlotIdOptions = {},
): string | undefined => {
  const date = day.get('date') as string
  const slots = day.get('slots') as Immutable.List<Immutable.Map<string, any>>

  const disabledSlots = (getDisabledSlotDates(state) || []) as string[]
  const bookableSlots = slots.filter(
    (slot) => !!slot && !disabledSlots.includes(createDisabledSlotId(slot, date)),
  )

  if (coreSlotId) {
    const match = bookableSlots.find((slot) => slot?.get('coreSlotId') === coreSlotId)
    if (match) {
      return match.get('id') as string
    }
    if (!fallbackToDefault) {
      return undefined
    }
  }

  const slot = bookableSlots.find((s) => !!s?.get('isDefault')) ?? bookableSlots.first()

  return slot ? (slot.get('id') as string) : undefined
}
